import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function hasPlatformAccess(user) {
  if (!user) return false;
  if (user.is_superuser || user.subscription_tier === 'admin' || user.lifetime_free) return true;
  return ['active', 'past_due', 'trial'].includes(user.subscription_status)
    && ['basic', 'pro'].includes(user.data_access_level);
}

export default function ChoosePlan() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  useEffect(() => {
    if (searchParams.get('checkout') === 'cancel') {
      toast('Checkout cancelled — pick a plan when you are ready', { icon: 'ℹ️' });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
    if (!loading && user && hasPlatformAccess(user)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/billing/plans');
        if (!cancelled) {
          setPlans((res.data.plans || []).filter(p => p.plan !== 'ad'));
          setTrialDays(res.data.trial_days ?? 7);
        }
      } catch (err) {
        console.error(err);
        toast.error('Could not load plans');
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startCheckout = async (priceId, planName) => {
    if (!priceId) {
      toast.error('Plan not configured on server');
      return;
    }
    setCheckoutPlan(planName);
    try {
      const res = await api.post('/api/billing/create-checkout-session', {
        price_id: priceId,
        billing_period: 'monthly',
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.message || 'Checkout failed');
      setCheckoutPlan(null);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <img src="/propscope-logo.png" alt="PropScope" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">Choose your plan</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-lg mx-auto">
            {plans.length === 1
              ? `Your role uses PropScope Basic at £49.99/mo with a ${trialDays}-day free trial. Card required — no charge until the trial ends.`
              : `Start with a ${trialDays}-day free trial. Card required — you will not be charged until the trial ends.`}
            {' '}Browse listings for free anytime; scores, AI insights, and charts unlock with a paid plan.
          </p>
        </div>

        {loadingPlans ? (
          <p className="text-slate-500 text-center">Loading plans…</p>
        ) : (
          <div className={`grid gap-5 ${plans.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-md mx-auto'}`}>
            {plans.map(plan => {
              const tier = plan.tiers?.[0];
              const isPro = plan.plan === 'pro';
              const isRecommended = plan.recommended;
              return (
                <div
                  key={plan.name}
                  className={`rounded-2xl border p-6 flex flex-col ${
                    isRecommended
                      ? 'border-emerald-500/50 bg-emerald-500/5'
                      : 'border-slate-800 bg-slate-900'
                  }`}
                >
                  {isRecommended && (
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">
                      Recommended for your role
                    </span>
                  )}
                  {isPro && !isRecommended && (
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">
                      Most popular
                    </span>
                  )}
                  <h2 className="text-xl font-bold text-white">{plan.name}</h2>
                  <p className="text-2xl font-bold text-emerald-400 mt-1">{plan.price}</p>
                  <p className="text-slate-500 text-xs mt-1">{trialDays}-day free trial</p>
                  <p className="text-slate-400 text-sm mt-3 mb-4">{plan.sub}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {(plan.features || []).map(f => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    disabled={!tier?.price_id || checkoutPlan === plan.name}
                    onClick={() => startCheckout(tier?.price_id, plan.name)}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl py-3 font-semibold transition-colors"
                  >
                    {checkoutPlan === plan.name ? 'Redirecting to Stripe…' : `Start ${trialDays}-day trial`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-slate-500 text-xs mt-6">
          Want to browse first?{' '}
          <button type="button" onClick={() => navigate('/properties')} className="text-emerald-400 hover:underline">
            Skip for now
          </button>
          {' '}— you can subscribe anytime from your account.
        </p>
      </div>
    </div>
  );
}
